import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CloseIcon from "../../../assets/icons/close.svg";
import APIVariables from "../../../services/api-variables";
import { handleCatch, isObject } from "../../../shared/helpers";
import { App } from "../../../routes/Constants";
import Loader from "../../common/Loader/Loader";
import { errorMessages } from "../../../constants/errorMessages.constants";
import RemoveConnectedPharmacistModal from "./modals/RemoveConPharmModal";
import useRoles from "../../../hooks/useRoles";
import QuitModal from "../../common/Modal/QuitModal";
import Axios from "../../../services/Axios";

const inputStyles =
  "mt-2.5 mb-[15px] w-full border-0 border-b border-[#c8c8c8] bg-[#f8f8f8] px-0 py-1 text-lg leading-[22px] text-[#646464] outline-none disabled:cursor-not-allowed";

const ConnectedPharmacistView = () => {
  const navigate = useNavigate();

  const selectedConnectedPharmacist = window.location.pathname.split("/")[2];
  const [form, setForm] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [modalIsOpen, setIsModalOpen] = useState(false);
  const { storageData } = useRoles();
  const [quitModalIsOpen, setIsOpenQuit] = useState(false);

  useEffect(() => {
    getData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = async () => {
    await Axios.get(
      APIVariables.HCP_CON_PHARMACIST + selectedConnectedPharmacist
    )
      .then((res) => {
        const { status, data } = res;
        if (status === 200) {
          setForm(data);
        }
      })
      .catch((error) => {
        handleCatch(error);
      });

    setIsLoading(false);
  };

  const openModalRemoveCP = (e) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const removeCP = async () => {
    setErrorMessage("");

    await Axios.delete(
      APIVariables.HCP_CON_PHARMACIST + selectedConnectedPharmacist
    )
      .then((res) => {
        const { status } = res;
        if (status === 200) {
          closeModal();
          navigate(App.CONNECTED_PHARMACISTS);
        }
      })
      .catch(function (error) {
        const errors = error.response.data;
        if (typeof errors === "string") {
          setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          Object.keys(errors).forEach((key) => {
            if (isObject(errors[key])) {
              Object.keys(errors[key]).forEach((secondKey) => {
                errorsArray.push(key + " - " + errors[key][secondKey][0]);
              });
            } else {
              errorsArray.push(key + " - " + errors[key]);
            }
          });
          setErrorMessage(errorsArray);
        }
      });
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  function closeForm() {
    setIsOpenQuit(true);
  }
  function closeModalQuit() {
    setIsOpenQuit(false);
  }
  function quitModal() {
    navigate(App.CONNECTED_PHARMACISTS);
  }

  if (isLoading) return <Loader />;

  return (
    <div
      className="min-h-screen overflow-y-auto  pt-[140px] pb-[100px]"
      data-testid="connected-pharmacists-view-page"
    >
      <div className="relative mx-auto max-w-[1100px] rounded-[10px] bg-white px-6 py-5 shadow-[0px_0px_40px_rgba(0,0,0,0.2)] sm:px-10">
        <button
          type="button"
          className="absolute right-[15px] top-[15px] cursor-pointer rounded-full bg-[#9ABDB8] p-[3px]"
          onClick={closeForm}
          aria-label="Close form"
          data-testid="connected-pharmacists-view-close-button"
        >
          <img src={CloseIcon} alt="" className="brightness-0 invert" />
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3 pr-10">
          <h1 className="select-category-txt mineShaft-color mt-2.5 pb-1.5">
            {`${form?.first_name} ${form?.last_name}`} - Pharmacist
          </h1>
          <button
            type="button"
            className="second-button red-button"
            onClick={(e) => openModalRemoveCP(e)}
            data-testid="connected-pharmacists-view-remove-button"
          >
            Remove
          </button>
        </div>
        <hr className="my-0 h-px border-0 bg-[#768692]" />

        <h2 className="title-details mineShaft-color mt-[30px] mb-[25px]">
          Pharmacist Details
        </h2>
        <div className="mb-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="view-cp-first-name">
                Pharmacist First Name:
              </label>
              <input
                id="view-cp-first-name"
                type="text"
                name="first_name"
                value={form?.first_name || ""}
                disabled
                className={inputStyles}
                data-testid="connected-pharmacists-view-first-name-input"
              />
            </div>
            <div>
              <label className="blue-label" htmlFor="view-cp-last-name">
                Pharmacist Last Name:
              </label>
              <input
                id="view-cp-last-name"
                type="text"
                name="last_name"
                value={form?.last_name || ""}
                disabled
                className={inputStyles}
                data-testid="connected-pharmacists-view-last-name-input"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="view-cp-reg-number">
                GPhC / PSNI Registration Number:
              </label>
              <input
                id="view-cp-reg-number"
                type="text"
                value={form?.registration_number || ""}
                disabled
                className={inputStyles}
                data-testid="connected-pharmacists-view-registration-input"
              />
            </div>
            <div>
              <label className="blue-label" htmlFor="view-cp-job-title">
                Job Title:
              </label>
              <input
                id="view-cp-job-title"
                type="text"
                name="job_title"
                value={form?.job_title || ""}
                disabled
                className={inputStyles}
                data-testid="connected-pharmacists-view-job-title-input"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="view-cp-pharmacy-name">
                Pharmacy Name:
              </label>
              <input
                id="view-cp-pharmacy-name"
                type="text"
                name="email"
                value={storageData?.pharmacy?.value || ""}
                disabled
                className={inputStyles}
                data-testid="connected-pharmacists-view-pharmacy-name-input"
              />
            </div>
            <div>
              <label className="blue-label" htmlFor="view-cp-email">
                Email Address: (A link will be sent to verify your email address)
              </label>
              <input
                id="view-cp-email"
                type="text"
                name="email"
                value={form?.email || ""}
                disabled
                className={inputStyles}
                data-testid="connected-pharmacists-view-email-input"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="blue-label" htmlFor="view-cp-pharmacy-address">
              Pharmacy address:
            </label>
            <input
              id="view-cp-pharmacy-address"
              type="text"
              value={storageData?.pharmacy?.address || ""}
              disabled
              className={inputStyles}
              data-testid="connected-pharmacists-view-pharmacy-address-input"
            />
          </div>
        </div>
      </div>
      {quitModalIsOpen && (
        <QuitModal
          quitModalIsOpen={quitModalIsOpen}
          closeModalQuit={closeModalQuit}
          quitModal={quitModal}
          description="Are you sure you want to cancel filling out this form? All progress will be lost and you will need to start again."
        />
      )}
      {modalIsOpen && (
        <RemoveConnectedPharmacistModal
          modalIsOpen={modalIsOpen}
          closeModal={closeModal}
          removePharmacist={removeCP}
          errorMessage={errorMessage}
        />
      )}
    </div>
  );
};
export default ConnectedPharmacistView;
